import * as React from 'react';
import ReactDOM from 'react-dom';
import CSSTransition from 'react-transition-group/CSSTransition';
import './NinoxPopup.css';

export const DEFAULT_ANIMATION_DURATION = 300;

export type EnterHandler = (node: HTMLElement, isAppearing: boolean) => void;
export type ExitHandler = (node: HTMLElement) => void;

export interface NinoxPopupProps {
    children: React.ReactNode;
    isVisible?: boolean;
    handleClose?: (e?) => void;
    animationDuration?: number;
    baseClass?: string;
    className?: string;
    animationClass?: string;
    unmountOnExit?: boolean;
    appear?: boolean;

    onEntered?: EnterHandler;
    onEntering?: EnterHandler;
    onEnter?: EnterHandler;

    onExited?: ExitHandler;
    onExit?: ExitHandler;
    onExiting?: ExitHandler;

    innerRef?: React.Ref<HTMLDivElement>;
}

/**
 *  to enable an animation you need to provide both `animationClass` and `animationDuration`
 *  read more details on https://reactcommunity.org/react-transition-group/css-transition
 *
 *  an example for a 500ms fade in and out animation would be
 *  .${anumationClass}-enter, .${anumationClass}-appear-active {
 *      opacity: 0;
 *  }
 *  .${anumationClass}-enter-active, .${anumationClass}-appear-active {
 *      opacity: 1;
 *      transition: opacity 500ms;
 *  }
 *  .${anumationClass}-exit {
 *      opacity: 1;
 *  }
 *  .${anumationClass}-exit-active {
 *      opacity: 0;
 *      transition: opacity 500ms;
 *  }
 */

export function NinoxPopup(props: NinoxPopupProps) {
    const animationDuration = props.animationDuration ?? DEFAULT_ANIMATION_DURATION;
    const animationClass = props.animationClass ?? 'popup-default-animation';
    const unmountOnExit = props.unmountOnExit ?? true;
    const appear = props.appear ?? true;
    const isVisible = props.isVisible ?? true;
    const [containerElement, setContainerElement] = React.useState<HTMLDivElement | null>(null);
    const className = props.className || '';
    const baseClass = props.baseClass ?? 'nx-popup';

    React.useLayoutEffect(() => {
        const cnt = document.createElement('div');

        document.body.appendChild(cnt);
        setContainerElement(cnt);

        return () => {
            document.body.removeChild(cnt);
        };
    }, []);

    function getPopupBody() {
        return (
            <div ref={props.innerRef} className={`${baseClass}__root ${className}`}>
                <div
                    className={`${baseClass}__catcher`}
                    onMouseDown={props.handleClose}
                    onTouchStart={props.handleClose}
                />
                {props.children}
            </div>
        );
    }

    if (containerElement) {
        if (animationDuration > 0 && animationClass) {
            return ReactDOM.createPortal(
                <CSSTransition
                    appear={appear}
                    classNames={animationClass}
                    in={isVisible}
                    timeout={animationDuration}
                    unmountOnExit={unmountOnExit}
                    onEnter={props.onEnter}
                    onEntered={props.onEntered}
                    onEntering={props.onEntering}
                    onExit={props.onExit}
                    onExited={props.onExited}
                    onExiting={props.onExiting}
                >
                    {getPopupBody()}
                </CSSTransition>,
                containerElement
            );
        }

        return isVisible ? ReactDOM.createPortal(getPopupBody(), containerElement) : null;
    }

    return null;
}
